import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';

interface FloatingChatState {
  isOpen: boolean;
  isMinimized: boolean;
  selectedRoomId: string | null;
  unreadCount: number;
  lastMessageTime: Date | null;
}

export const useFloatingChat = () => {
  const { user } = useAuthStore();
  const [state, setState] = useState<FloatingChatState>({
    isOpen: false,
    isMinimized: false,
    selectedRoomId: null,
    unreadCount: 0,
    lastMessageTime: null,
  });

  // Persistir estado no localStorage
  useEffect(() => {
    if (user) {
      const savedState = localStorage.getItem(`floating-chat-${user.id}`);
      if (savedState) {
        try {
          const parsed = JSON.parse(savedState);
          setState(prev => ({ ...prev, ...parsed }));
        } catch (error) {
          console.error('Erro ao carregar estado do chat:', error);
        }
      }
    }
  }, [user]);

  // Salvar estado no localStorage
  useEffect(() => {
    if (user) {
      localStorage.setItem(`floating-chat-${user.id}`, JSON.stringify({
        isOpen: state.isOpen,
        isMinimized: state.isMinimized,
        selectedRoomId: state.selectedRoomId,
      }));
    }
  }, [state.isOpen, state.isMinimized, state.selectedRoomId, user]);

  const openChat = () => {
    setState(prev => ({ ...prev, isOpen: true, isMinimized: false }));
  };

  const closeChat = () => {
    setState(prev => ({ ...prev, isOpen: false }));
  };

  const toggleChat = () => {
    setState(prev => ({ 
      ...prev, 
      isOpen: !prev.isOpen, 
      isMinimized: prev.isOpen ? false : prev.isMinimized 
    }));
  };

  const minimizeChat = () => {
    setState(prev => ({ ...prev, isMinimized: true }));
  };

  const maximizeChat = () => {
    setState(prev => ({ ...prev, isMinimized: false }));
  };

  const toggleMinimize = () => {
    setState(prev => ({ ...prev, isMinimized: !prev.isMinimized }));
  };

  const selectRoom = (roomId: string | null) => {
    setState(prev => ({ ...prev, selectedRoomId: roomId }));
  };

  const updateUnreadCount = (count: number | ((prev: number) => number)) => {
    setState(prev => ({ 
      ...prev, 
      unreadCount: typeof count === 'function' ? count(prev.unreadCount) : count 
    }));
  };

  const updateLastMessageTime = (time: Date) => {
    setState(prev => ({ ...prev, lastMessageTime: time }));
  };

  const resetUnreadCount = () => {
    setState(prev => ({ ...prev, unreadCount: 0 }));
  };

  return {
    ...state,
    openChat,
    closeChat,
    toggleChat,
    minimizeChat,
    maximizeChat,
    toggleMinimize,
    selectRoom,
    updateUnreadCount,
    updateLastMessageTime,
    resetUnreadCount,
  };
};
